<template>
  <div class="performance-charts-section iq-card">
    <h4 class="section-title text-primary font-weight-bold mb-2 pb-2 border-bottom">Performance Charts</h4>

    <!-- Chart 1: RPM Profile -->
    <div class="chart-container mb-3" ref="rpmChartContainer">
      <h6 class="chart-title text-secondary text-uppercase font-weight-bold small mb-2">
        <i class="ri-settings-3-line mr-2"></i>RPM Profile
      </h6>
      <LineChart
        chart-id="rpm-chart"
        :data="rpmChartData"
        :datasets="rpmDatasets"
        timestamp-key="timestamp"
        y-label="RPM"
        :height="300"
        :loading="loading"
        :range="chartRange"
        file-name="voyage-rpm"
      />
    </div>

    <!-- Chart 2: Fuel Rate Profile -->
    <div class="chart-container mb-3" ref="fuelChartContainer">
      <h6 class="chart-title text-secondary text-uppercase font-weight-bold small mb-2">
        <i class="ri-drop-line mr-2"></i>Fuel Rate Profile
      </h6>
      <LineChart
        chart-id="fuel-chart"
        :data="fuelChartData"
        :datasets="fuelDatasets"
        timestamp-key="timestamp"
        y-label="Fuel Cons (L/hr)"
        :show-area="true"
        :height="300"
        :loading="loading"
        :range="chartRange"
        file-name="voyage-fuel-rate"
      />
    </div>

    <!-- Chart 3: Speed + Wave -->
    <div class="chart-container" ref="speedWaveChartContainer">
      <h6 class="chart-title text-secondary text-uppercase font-weight-bold small mb-2">
        <i class="ri-speed-line mr-2"></i>Speed & Wave Profile
      </h6>
      <CombinedChart
        chart-id="speed-wave-chart"
        :data="speedWaveChartData"
        :datasets="speedWaveDatasets"
        timestamp-key="timestamp"
        left-y-label="Speed (knots)"
        right-y-label="Wave Height (m)"
        :height="300"
        :loading="loading"
        :range="chartRange"
        file-name="voyage-speed-wave"
      />
    </div>
  </div>
</template>

<script>
import { apiDatetimeToUnix } from '@/util/apiDatetime';
import { useVoyageData } from '@/views/voyage/composables/useVoyageData';
import { extractRpmSeries, extractFlowmeterSeries, extractGpsSeries } from '@/views/voyage/utils/chartExtractors';
import LineChart from './charts/LineChart.vue';
import CombinedChart from './charts/CombinedChart.vue';

const { getTelemetryAggregationParams } = useVoyageData();

export default {
  name: 'PerformanceCharts',
  components: {
    LineChart,
    CombinedChart,
  },
  props: {
    voyage: {
      type: Object,
      default: null,
    },
    rpmData: {
      type: Object,
      default: null,
    },
    flowmeterData: {
      type: Object,
      default: null,
    },
    gpsData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    chartRange() {
      if (!this.voyage?.mvygStartTime || !this.voyage?.mvygEndTime) return null;
      const start = apiDatetimeToUnix(this.voyage.mvygStartTime);
      const end = apiDatetimeToUnix(this.voyage.mvygEndTime);
      const unit = getTelemetryAggregationParams(this.voyage).aggregatedUnit.toLowerCase();
      return [start * 1000, end * 1000, unit];
    },
    rpmChartData() {
      return extractRpmSeries(this.rpmData);
    },
    rpmDatasets() {
      return [
        { name: 'Port RPM', dataKey: 'portRpm', color: '#8B5CF6' },
        { name: 'Starboard RPM', dataKey: 'starboardRpm', color: '#EC4899' },
      ];
    },
    fuelChartData() {
      return extractFlowmeterSeries(this.flowmeterData);
    },
    fuelDatasets() {
      return [
        { name: 'ME Fuel', dataKey: 'totalFuelCons', color: '#dc3545' },
      ];
    },
    speedWaveChartData() {
      return extractGpsSeries(this.gpsData);
    },
    speedWaveDatasets() {
      return [
        {
          name: 'Speed', dataKey: 'speed', color: '#0d6efd', type: 'line',
        },
        {
          name: 'Wave Height', dataKey: 'waveHeight', color: '#0dcaf0', type: 'bar',
        },
      ];
    },
  },
};
</script>

<style scoped>
.performance-charts-section {
  padding: 0.75rem;
  background: #fff;
  border-radius: 0.375rem;
}

.performance-charts-section.iq-card {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  box-shadow: none;
}

.chart-container {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 0.75rem;
  position: relative;
}

.chart-title {
  margin-bottom: 0.5rem;
}
</style>
